/*
  This component is currently set up to support USD currency, it could be adapted though.
  It uses react-number-format for input formatting.
  https://www.npmjs.com/package/react-number-format#values-object
*/

import React, { type FocusEvent } from 'react';
import NumberFormat, { type NumberFormatValues } from 'react-number-format';
import TextField from '@mui/material/TextField';
import kebabCase from 'lodash/kebabCase';

const NUMBER_FORMAT_RETURNED_VALUES = {
  floatValue: 'floatValue',
  formattedValue: 'formattedValue',
  value: 'value',
};

type CurrencyInputProps = {
  amount?: number | string;
  disabled?: boolean;
  error?: boolean;
  errorHelperText?: string;
  label?: string;
  required?: boolean;
  returnedValue?: keyof typeof NUMBER_FORMAT_RETURNED_VALUES;
  setAmount?: (...event: unknown[]) => void;
};

function CurrencyInput({
  amount = 0,
  disabled = false,
  error = false,
  errorHelperText = '',
  label = '',
  required = false,
  returnedValue = NUMBER_FORMAT_RETURNED_VALUES.formattedValue as keyof typeof NUMBER_FORMAT_RETURNED_VALUES,
  setAmount = () => {},
  ...props
}: CurrencyInputProps) {
  const handleChange = (values: NumberFormatValues) => {
    setAmount(
      values[returnedValue as keyof typeof NUMBER_FORMAT_RETURNED_VALUES],
    );
  };

  // Select the entire value on focus
  const handleFocus = (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => event?.target?.select();

  const id = `currency-input-${kebabCase(label)}`;

  return (
    <NumberFormat
      customInput={TextField}
      data-testid={id}
      decimalScale={2}
      disabled={disabled}
      error={error}
      fixedDecimalScale
      fullWidth
      helperText={error ? errorHelperText : ''}
      id={id}
      inputProps={{ inputMode: 'decimal' }}
      label={label}
      onFocus={handleFocus}
      onValueChange={handleChange}
      prefix="$"
      required={required}
      tabIndex={0}
      thousandSeparator
      value={amount}
      {...props}
    />
  );
}

export default CurrencyInput;
